/**
 * Tab Registry - Central registry of all available tab components and their default configs
 *
 * This allows drawers to pick and choose which tabs they need without
 * having to import and configure each component manually.
 */

import FinancialTotals from 'components/Accounting/FinancialTotals'
import CommentsInput from 'components/Comments/CommentsInput'
import CommandCenterTasks from 'components/Creative/CommandCenterTasks'
import IntegrationChecklistTable from 'components/DataGrid/components/IntegrationChecklistTable'
import KickoffCall from 'components/DataGrid/components/KickoffCall'
import ReviewDeal from 'components/DealApproval/ReviewDeal'
import OptimizationsContainer from 'components/Optimizations/OptimizationsContainer'
import OrgDealInfo from 'components/Organizations/OrgDealInfo'
import RenderCampaign from 'components/StrategyCampaigns/RenderCampaign'
import type { TabDefinition, TabType } from './types'

// Lazy import for NCO-specific component to avoid circular deps
const getOnboardingTasks = () => import('components/NCO/OnboardingTasks').then(m => m.default)

// Lazy import for MTB-specific component
const getRenderMonthlyTaskList = () => import('components/MTB/RenderMonthlyTaskList').then(m => m.default)

/**
 * Registry of all available tab types with their default configurations
 */
export const TAB_REGISTRY: Record<TabType, TabDefinition> = {
  onboarding: {
    label: 'main',
    display: 'Onboarding Tasks',
    Component: null as any, // Set dynamically
    needsEditing: false,
  },
  commandCenter: {
    label: 'tasks',
    display: 'Command Center Tasks',
    Component: CommandCenterTasks,
    needsEditing: true,
    saveKind: 'all',
  },
  comments: {
    label: 'comments',
    display: 'Comments',
    Component: CommentsInput,
    needsEditing: false,
  },
  reviewDeal: {
    label: 'review',
    display: 'Review Deal',
    Component: ReviewDeal,
    needsEditing: false,
  },
  kickoffCall: {
    label: 'kickoff',
    display: 'Kickoff Call',
    Component: KickoffCall,
    needsEditing: false,
  },
  integration: {
    label: 'integration',
    display: 'Integration',
    Component: IntegrationChecklistTable,
    needsEditing: false,
  },
  seo: {
    label: 'seo',
    display: 'SEO',
    Component: IntegrationChecklistTable,
    needsEditing: true,
    saveKind: 'checklist',
  },
  optimizations: {
    label: 'main',
    display: 'Optimizations',
    Component: OptimizationsContainer,
    needsEditing: false,
  },
  monthlyTasksSeo: {
    label: 'seo',
    display: 'SEO',
    Component: null as any, // Set dynamically
    needsEditing: false,
  },
  monthlyTasksAm: {
    label: 'am',
    display: 'AM',
    Component: null as any, // Set dynamically
    needsEditing: false,
  },
  monthlyTasksDms: {
    label: 'dms',
    display: 'DMS',
    Component: null as any, // Set dynamically
    needsEditing: false,
  },
  renderCampaign: {
    label: 'strategy',
    display: 'Render Campaign',
    Component: RenderCampaign,
    needsEditing: false,
  },
  dealsInfo: {
    label: 'deals',
    display: 'Deals',
    Component: OrgDealInfo,
    needsEditing: false,
  },
  financial: {
    label: 'details',
    display: 'Details',
    Component: FinancialTotals,
    needsEditing: false,
  },
}

/**
 * Stable empty object to prevent unnecessary re-renders
 */
export const EMPTY_FORM_DATA: Record<string, any> = {}

/**
 * Default context filters for different drawer types
 */
export const CONTEXT_FILTERS = {
  nco: ['nco'] as const,
  mtb: ['mtb'] as const,
} as const
